<template>
  <div class="flex flex-col h-full">
    <div class="flex items-center gap-2 pb-3 shrink-0">
      <div class="relative flex-1 max-w-xs">
        <FontAwesomeIcon
          :icon="['fas', 'magnifying-glass']"
          class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
        />
        <input
          v-model="search"
          type="text"
          placeholder="Search subjects"
          class="w-full pl-8 pr-3 py-1.5 text-sm text-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          @input="onSearchInput"
        />
      </div>
      <MultiSelectDropdown
        v-model="typeFilter"
        :options="typeOptions"
        placeholder="All types"
        @change="reload"
      />
      <MultiSelectDropdown
        v-model="statusFilter"
        :options="statusOptions"
        placeholder="All statuses"
        @change="reload"
      />
      <MultiSelectDropdown
        v-model="topicFilter"
        :options="topicOptions"
        placeholder="All topics"
        searchable
        @change="reload"
      />
      <FilterDropdown
        v-model="transcriptFilter"
        :options="transcriptOptions"
        placeholder="Any transcript"
        clearable
        @change="reload"
      />
      <input
        v-model="dateFrom"
        type="date"
        title="From date"
        class="px-2 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        @change="reload"
      />
      <span class="text-xs text-gray-600">to</span>
      <input
        v-model="dateTo"
        type="date"
        title="To date"
        class="px-2 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        @change="reload"
      />
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto">
      <div v-if="loading" class="divide-y divide-gray-100">
        <div v-for="n in 5" :key="n" class="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div class="w-9 h-8 bg-gray-100 rounded"></div>
          <div class="flex-1 space-y-2">
            <div class="h-3 bg-gray-100 rounded w-1/2"></div>
            <div class="h-2.5 bg-gray-100 rounded w-1/3"></div>
          </div>
        </div>
      </div>

      <div v-else-if="engagements.length === 0" class="py-10 text-center">
        <p class="text-sm text-gray-800">No engagements found</p>
        <p class="text-xs text-gray-600 mt-1">
          {{ hasActiveFilters ? 'Try clearing the search or filters.' : 'Engagements will appear here once they are logged.' }}
        </p>
      </div>

      <div v-else>
        <div
          v-for="eng in engagements"
          :key="eng.id"
          class="flex items-start gap-3 px-4 py-2.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors"
          @click="goToEngagement(eng.id)"
        >
          <div class="w-9 shrink-0 text-center">
            <div class="text-sm font-semibold text-gray-900 leading-5">{{ dayOfMonth(eng.date) }}</div>
            <div class="text-[11px] font-medium text-gray-600 uppercase leading-none">{{ monthAbbr(eng.date) }}</div>
            <div class="text-[11px] text-gray-600 leading-none mt-0.5">{{ year(eng.date) }}</div>
          </div>

          <div class="flex-1 min-w-0">
            <router-link
              :to="`/engagement/${eng.id}`"
              class="sensitive block text-sm leading-5 font-semibold text-gray-900 hover:underline"
              @click.stop
            >
              {{ eng.subject || 'Untitled engagement' }}
            </router-link>
            <div class="flex items-center gap-2 text-xs leading-4 text-gray-600 mt-1">
              <Tag v-if="eng.engagement_type_name" :label="eng.engagement_type_name" color="gray" size="sm" />
              <span v-if="eng.entity_name" class="inline-flex items-center gap-1 font-medium text-gray-800">
                <FontAwesomeIcon :icon="getEntityIcon(eng)" class="w-3 h-3" />
                <span class="sensitive">{{ eng.entity_name }}</span>
              </span>
              <span v-if="eng.engagement_channel_name">· {{ eng.engagement_channel_name }}</span>
              <span v-if="eng.engagement_participants?.length">· {{ attendeeLabel(eng) }}</span>
            </div>
            <p
              v-if="summaryText(eng)"
              class="sensitive text-xs text-gray-600 mt-0.5 line-clamp-1"
              data-demo-blur
            >
              {{ summaryText(eng) }}
            </p>
          </div>

          <div class="flex items-center gap-2 shrink-0">
            <Tag
              v-if="eng.engagement_transcript"
              label="Transcript"
              color="default"
              size="sm"
              clickable
              :title="eng.engagement_transcription_summary ? 'View transcript summary' : 'Open engagement'"
              @click.stop="openTranscript(eng)"
            >
              <template #prefix>
                <FontAwesomeIcon :icon="['fas', 'file-lines']" class="w-3 h-3" />
              </template>
            </Tag>
            <Tag :label="statusLabel(eng.status)" :color="statusColor(eng.status)" size="sm" />
            <button
              type="button"
              class="text-gray-600 hover:text-red-dark"
              title="Delete engagement"
              @click.stop="confirmDelete(eng)"
            >
              <FontAwesomeIcon :icon="['fas', 'trash-can']" class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div v-if="hasMore" class="px-4 py-2 text-center border-t border-gray-100">
          <button
            type="button"
            class="text-xs font-medium text-gray-600 hover:text-gray-900"
            :disabled="loadingMore"
            @click="loadMore"
          >
            {{ loadingMore ? 'Loading…' : `Show more (${engagements.length} of ${totalCount})` }}
          </button>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="transcriptEngagement"
        class="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
        @click.self="transcriptEngagement = null"
      >
        <div class="bg-white rounded-lg shadow p-5 mx-52">
          <Transcript_Summary :transcript-summary="transcriptEngagement.engagement_transcription_summary" :engagement-id="transcriptEngagement.id" />
          <div class="flex justify-end mt-4 pt-3 border-t">
            <button type="button" class="btn-primary" @click="transcriptEngagement = null">Close</button>
          </div>
        </div>
      </div>
    </Teleport>

    <ConfirmationModal
      :is-visible="deleteTarget !== null"
      message="Are you sure you want to delete this engagement?"
      @confirm="deleteEngagement"
      @cancel="deleteTarget = null"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue';
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import { getEntityIcon } from '@/utils/entityIcons';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import Transcript_Summary from '@/components/widgetcomponents/JSON_Editors/Transcript_Summary.vue';

const STATUS_LABELS = {
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  RESCHEDULED: 'Rescheduled',
  NO_SHOW: 'No Show',
};

const STATUS_COLORS = {
  SCHEDULED: 'blue',
  COMPLETED: 'secondary',
  RESCHEDULED: 'amber',
  CANCELLED: 'gray',
  NO_SHOW: 'gray',
};

const PAGE_SIZE = 25;

export default {
  name: 'EngagementTable',
  components: {
    FontAwesomeIcon,
    FilterDropdown,
    MultiSelectDropdown,
    Tag,
    ConfirmationModal,
    Transcript_Summary,
  },
  props: {
    entityId: {
      type: Number,
      default: null,
    },
    includeDescendants: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      engagements: [],
      totalCount: 0,
      loading: true,
      loadingMore: false,
      search: '',
      searchTimer: null,
      typeFilter: [],
      statusFilter: [],
      topicFilter: [],
      transcriptFilter: null,
      dateFrom: '',
      dateTo: '',
      typeOptions: [],
      topicOptions: [],
      transcriptEngagement: null,
      deleteTarget: null,
    };
  },
  computed: {
    statusOptions() {
      return Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));
    },
    transcriptOptions() {
      return [
        { value: 'has', label: 'Has transcript' },
        { value: 'none', label: 'No transcript' },
      ];
    },
    hasMore() {
      return this.engagements.length < this.totalCount;
    },
    hasActiveFilters() {
      return Boolean(
        this.search || this.typeFilter.length || this.statusFilter.length
        || this.topicFilter.length || this.transcriptFilter || this.dateFrom || this.dateTo,
      );
    },
    filterModel() {
      const model = {};
      if (this.search) {
        model.subject = { filterType: 'text', filter: this.search };
      }
      if (this.typeFilter.length) {
        model.engagement_type_id = { filterType: 'set', values: this.typeFilter };
      }
      if (this.statusFilter.length) {
        model.status = { filterType: 'set', values: this.statusFilter };
      }
      if (this.topicFilter.length) {
        model.engagement_topics = { filterType: 'set', values: this.topicFilter };
      }
      if (this.transcriptFilter) {
        model.zoomMeetingTranscription = {
          filterType: 'set',
          values: [this.transcriptFilter === 'has' ? 'Has Value' : 'No Value'],
        };
      }
      if (this.dateFrom || this.dateTo) {
        model.date = { filterType: 'date', dateFrom: this.dateFrom || null, dateTo: this.dateTo || null };
      }
      return model;
    },
  },
  watch: {
    entityId() {
      this.reload();
    },
  },
  mounted() {
    this.fetchTypeOptions();
    this.fetchTopicOptions();
    this.reload();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    getEntityIcon,
    async fetchTypeOptions() {
      const response = await axios.get('/api/engagementtypes/');
      const rows = response.data.results || response.data;
      this.typeOptions = rows.map(t => ({ value: t.id, label: t.name }));
    },
    async fetchTopicOptions() {
      const response = await axios.get('/api/engagementtopics/');
      const rows = response.data.results || response.data;
      this.topicOptions = rows
        .map(t => ({ value: t.id, label: t.name }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    async fetchPage(startRow) {
      const payload = {
        start_row: startRow,
        end_row: startRow + PAGE_SIZE,
        sort_model: [{ colId: 'date', sort: 'desc' }],
        filter_model: this.filterModel,
      };
      if (this.entityId) {
        payload.entity_id = this.entityId;
        payload.include_descendants = this.includeDescendants;
      }
      const response = await axios.post('/api/engagements/list/', payload);
      return response.data;
    },
    async reload() {
      this.loading = true;
      const data = await this.fetchPage(0);
      this.engagements = data.rowData || [];
      this.totalCount = data.lastRow ?? this.engagements.length;
      this.loading = false;
    },
    async loadMore() {
      this.loadingMore = true;
      const data = await this.fetchPage(this.engagements.length);
      this.engagements.push(...(data.rowData || []));
      this.totalCount = data.lastRow ?? this.engagements.length;
      this.loadingMore = false;
    },
    refreshData() {
      this.reload();
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 300);
    },
    goToEngagement(id) {
      this.$router.push(`/engagement/${id}`);
    },
    openTranscript(eng) {
      if (eng.engagement_transcription_summary) {
        this.transcriptEngagement = eng;
      } else {
        this.goToEngagement(eng.id);
      }
    },
    confirmDelete(eng) {
      this.deleteTarget = eng;
    },
    async deleteEngagement() {
      await axios.delete(`/api/engagements/${this.deleteTarget.id}/`);
      this.deleteTarget = null;
      this.reload();
    },
    dayOfMonth(dateStr) {
      return new Date(dateStr + 'T00:00:00').getDate();
    },
    year(dateStr) {
      return new Date(dateStr + 'T00:00:00').getFullYear();
    },
    monthAbbr(dateStr) {
      return new Date(dateStr + 'T00:00:00').toLocaleString('en-US', { month: 'short' });
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || '';
    },
    statusColor(status) {
      return STATUS_COLORS[status] || 'gray';
    },
    attendeeLabel(eng) {
      const n = eng.engagement_participants.length;
      return n === 1 ? '1 attendee' : `${n} attendees`;
    },
    summaryText(eng) {
      const raw = eng.engagement_transcription_summary;
      if (!raw) return eng.note || null;
      let parsed = raw;
      if (typeof raw === 'string') {
        try {
          parsed = JSON.parse(raw);
        } catch {
          return raw;
        }
      }
      if (parsed && typeof parsed === 'object') {
        if (parsed.overallSummary) return parsed.overallSummary;
        for (const section of Object.values(parsed)) {
          if (section?.overallSummary) return section.overallSummary;
        }
      }
      return eng.note || null;
    },
  },
};
</script>
